import { type intlFormatPropTypes } from "react-intl";

import type { Country } from "../../AddressForm/AddressForm.types";
import type { VatFeeProfile } from "../../app-main/selectors/fee-profile.selectors";
import {
  findTaxIdValidator,
  formatTaxTypeName,
  getTaxIdAbbreviation,
  getTaxTypeName,
} from "../TaxDetails/TaxDetails.utils";

import { TAX_DETAILS_RATE_MAX_DECIMALS } from "./TaxDetailsFormSection.consts";
import { TAX_DETAILS_FORM_MESSAGES } from "./TaxDetailsFormSection.messages";
import type {
  TaxDetailsFormValues,
  TaxIdRegion,
} from "./TaxDetailsFormSection.types";

type FormatMessage = Pick<intlFormatPropTypes, "formatMessage">;

/**
 * Whether a tax ID is in the format its region issues, or merely present where we
 * hold no format for the region.
 */
const isTaxIdValid = ({
  country,
  isSubregion,
  state,
  taxId,
}: TaxIdRegion & { taxId: string }): boolean => {
  const compactTaxId = taxId.trim();

  if (0 === compactTaxId.length) {
    return false;
  }

  const validator = findTaxIdValidator({ country, isSubregion, state });

  if (validator) {
    try {
      return validator.validate(compactTaxId).isValid;
    } catch {
      return false;
    }
  }

  return true;
};

/**
 * The tax ID as its region stores it — "DE 327 990 207" becomes "327990207": no
 * country prefix, no separators. Where we hold no format for the region, only the
 * surrounding whitespace goes.
 */
const compactTaxId = ({
  country,
  isSubregion,
  state,
  taxId,
}: TaxIdRegion & { taxId: string }): string => {
  const trimmedTaxId = taxId.trim();
  const validator = findTaxIdValidator({ country, isSubregion, state });

  if (!validator) {
    return trimmedTaxId;
  }

  try {
    return validator.compact(trimmedTaxId);
  } catch {
    return trimmedTaxId;
  }
};

/**
 * Whether to render the ID and rate fields at all. Three states look alike here
 * but read very differently to the guest: the fees have not landed, a province is
 * still to be picked, and this region genuinely levies no such tax. Only the last
 * is an absence, so the first two keep the fields rather than report one.
 */
const isScopedTaxAvailable = ({
  areFeesLoading,
  isRegionRequired,
  isSubregion,
  regionLeviesScopedTax,
}: {
  areFeesLoading: boolean;
  isRegionRequired: boolean;
  isSubregion: boolean;
  regionLeviesScopedTax: boolean;
}): boolean =>
  areFeesLoading ||
  (!isRegionRequired && (regionLeviesScopedTax || !isSubregion));

/**
 * The narrowest name available for the tax being registered: the region's own tax
 * where a region has resolved it; otherwise everything the country levies at this
 * level — "GST/HST" until a province narrows it to "HST" — falling back to the
 * saved registration's own types while the fees are still in flight.
 */
const resolveTaxTypeName = ({
  areFeesLoading,
  country,
  countryTaxTypes,
  intl,
  savedTaxTypes,
  taxTypeKey,
}: {
  areFeesLoading: boolean;
  country?: string;
  countryTaxTypes: string[];
  intl: FormatMessage;
  savedTaxTypes: string[];
  taxTypeKey?: string;
}): string => {
  if (taxTypeKey) {
    return getTaxTypeName({ intl, taxType: taxTypeKey });
  }

  // The sentinel a saved registration carries means "the region named no tax for
  // this slot", which is exactly the case the country's own number answers.
  return formatTaxTypeName({
    intl,
    taxIdAbbreviation: getTaxIdAbbreviation({ country }),
    taxTypes: areFeesLoading ? savedTaxTypes : countryTaxTypes,
  });
};

/**
 * What to call the place with no registration to offer — the province by name
 * where one is picked, otherwise the country.
 */
const getRegionName = ({
  intl,
  selectedCountry,
  state,
}: {
  intl: FormatMessage;
  selectedCountry?: Country;
  state?: string;
}): string =>
  selectedCountry?.zones?.find((zone) => zone.code === state)?.name ??
  selectedCountry?.name ??
  intl.formatMessage(TAX_DETAILS_FORM_MESSAGES.defaultRegionName);

/**
 * The country's own word for its subdivision — "province", "state", "county" —
 * lowercased because it lands mid-sentence.
 */
const getRegionLabel = ({
  intl,
  selectedCountry,
}: {
  intl: FormatMessage;
  selectedCountry?: Country;
}): string =>
  (
    selectedCountry?.labels?.zone ??
    intl.formatMessage(TAX_DETAILS_FORM_MESSAGES.defaultRegionLabel)
  ).toLowerCase();

/**
 * The region subtag of a locale — the "GB" of "en-GB". A bare language tag names
 * no country, so there is nothing to default the form's country to.
 */
const getLocaleCountry = (locale?: string): string | undefined => {
  const [, region] = locale?.split("-") ?? [];

  return region;
};

/** Whether the form is waiting on a province before it can name anything. */
const isRegionSelectionRequired = ({
  areFeesLoading,
  hasCountryRegions,
  isRegionResolved,
}: {
  areFeesLoading: boolean;
  hasCountryRegions: boolean;
  isRegionResolved: boolean;
}): boolean => !areFeesLoading && !isRegionResolved && hasCountryRegions;

/** Only a host declares a rate; a guest's registration carries none. */
const isRateFieldVisible = ({ isHost }: { isHost: boolean }) => isHost;

/**
 * An account pinned to a country cannot change it — a second country needs a
 * second account, the same rule add-a-space enforces.
 */
const isCountryLocked = ({ accountCountry }: { accountCountry?: string }) =>
  Boolean(accountCountry);

/**
 * The stored fraction as the percentage the host typed. Rounded because
 * `0.07 * 100` is `7.000000000000001`, which would otherwise land in the field.
 */
const formatTaxRate = (vatPercent?: number): string =>
  Number.isFinite(vatPercent)
    ? String(
        Number(
          (Number(vatPercent) * 100).toFixed(TAX_DETAILS_RATE_MAX_DECIMALS),
        ),
      )
    : "";

/**
 * What the form opens with: the existing registration when updating, and nothing
 * but a country when adding.
 */
const buildTaxDetailsFormDefaults = ({
  accountCountry,
  isHost,
  localeCountry,
  vatProfile,
}: {
  accountCountry?: string;
  isHost: boolean;
  localeCountry?: string;
  vatProfile?: VatFeeProfile;
}): TaxDetailsFormValues => ({
  businessName: vatProfile?.business_name ?? "",
  taxId: vatProfile?.vat_id ?? "",
  taxRate: isHost ? formatTaxRate(vatProfile?.vat_percent) : "",
  country: vatProfile?.country || accountCountry || localeCountry || "",
  street: vatProfile?.street ?? "",
  unit: vatProfile?.unit ?? "",
  city: vatProfile?.city ?? "",
  state: vatProfile?.state ?? "",
  zip: vatProfile?.zip ?? "",
  isConfirmed: false,
});

export {
  buildTaxDetailsFormDefaults,
  compactTaxId,
  formatTaxRate,
  getLocaleCountry,
  getRegionLabel,
  getRegionName,
  isCountryLocked,
  isRateFieldVisible,
  isRegionSelectionRequired,
  isScopedTaxAvailable,
  isTaxIdValid,
  resolveTaxTypeName,
};
